import { useMemo, useEffect } from "react";
import { Box, TextField, MenuItem } from "@mui/material";
import useProductTypes from "../hooks/useProductTypes";

export default function TypeSelector({ values, setValues }) {
  const { types, subTypes, subSubTypes } = useProductTypes();

  const metafieldType = values?.metafields?.type;
  const metafieldSubtype = values?.metafields?.subtype;
  const metafieldSubsubtype = values?.metafields?.subsubtype;

  const selectedType = useMemo(() => {
    if (!metafieldType) return null;
    return types.find((t) => t.name === metafieldType) ?? null;
  }, [types, metafieldType]);

  const selectedSubtype = useMemo(() => {
    if (!metafieldSubtype || !selectedType) return null;
    return subTypes.find(
      (t) => t.name === metafieldSubtype && t.parentTypeId === selectedType?.id
    ) ?? null;
  }, [subTypes, metafieldSubtype, selectedType]);

  const selectedSubsubtype = useMemo(() => {
    if (!metafieldSubsubtype || !selectedSubtype) return null;
    return subSubTypes.find(
      (t) =>
        t.name === metafieldSubsubtype &&
        t.parentSubtypeId === selectedSubtype?.id
    ) ?? null;
  }, [subSubTypes, metafieldSubsubtype, selectedSubtype]);

  const filteredSubTypes = useMemo(() => {
    if (!selectedType) return [];
    return subTypes.filter((t) => t.parentTypeId === selectedType.id);
  }, [subTypes, selectedType]);

  const filteredSubSubTypes = useMemo(() => {
    if (!selectedSubtype) return [];
    return subSubTypes.filter((t) => t.parentSubtypeId === selectedSubtype.id);
  }, [subSubTypes, selectedSubtype]);

  // Validate type
  useEffect(() => {
    if (!types.length || !metafieldType) return;
    if (!types.some((t) => t.name === metafieldType))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), type: "", subtype: "", subsubtype: "" },
      }));
  }, [types, metafieldType, setValues]);

  // Validate subtype
  useEffect(() => {
    if (!subTypes.length || !metafieldSubtype) return;
    if (!filteredSubTypes.some((t) => t.name === metafieldSubtype))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), subtype: "", subsubtype: "" },
      }));
  }, [filteredSubTypes, metafieldSubtype, subTypes, setValues]);

  // Validate subsubtype
  useEffect(() => {
    if (!subSubTypes.length || !metafieldSubsubtype) return;
    if (!filteredSubSubTypes.some((t) => t.name === metafieldSubsubtype))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), subsubtype: "" },
      }));
  }, [filteredSubSubTypes, metafieldSubsubtype, subSubTypes, setValues]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 2,
      }}
    >
      <TextField
        select
        label="Type"
        value={selectedType?.name ?? ""}
        onChange={(e) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              type: e.target.value,
              subtype: "",
              subsubtype: "",
            },
          }))
        }
        fullWidth
        size="small"
      >
        {types.map((opt) => (
          <MenuItem key={opt.id} value={opt.name}>
            {opt.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Subtype"
        value={selectedSubtype?.name ?? ""}
        onChange={(e) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              subtype: e.target.value,
              subsubtype: "",
            },
          }))
        }
        fullWidth
        size="small"
        disabled={!metafieldType || filteredSubTypes.length === 0}
      >
        {filteredSubTypes.map((opt) => (
          <MenuItem key={opt.id} value={opt.name}>
            {opt.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Subsubtype"
        value={selectedSubsubtype?.name ?? ""}
        onChange={(e) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              subsubtype: e.target.value,
            },
          }))
        }
        fullWidth
        size="small"
        disabled={!metafieldSubtype || filteredSubSubTypes.length === 0}
      >
        {filteredSubSubTypes.map((opt) => (
          <MenuItem key={opt.id} value={opt.name}>
            {opt.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
